import { Ship, Truck } from "lucide-react";

import { cn } from "@/lib/utils";
import type { RegimePaese } from "@/types/api";

/** Capienza standard di un camion/container (pallet). */
export const CAPIENZA_STD = 33;

/** Palette regime doganale — condivisa tra card kanban e proposta (spec card §5). */
export const REGIME_TAG: Record<RegimePaese, { label: string; cls: string }> = {
  italia: { label: "Nazionale", cls: "bg-slate-100 text-slate-800" },
  ue: { label: "Intra CEE", cls: "bg-sky-100 text-sky-800" },
  extra_ue: { label: "Extra CEE", cls: "bg-amber-100 text-amber-800" },
  bielorussia: { label: "Bielorussia", cls: "bg-violet-100 text-violet-800" },
};

/** Verde ≤33, ambra 34-35 (sovrapposti ammessi), rosso >35. */
export function capacityColor(pal: number, cap: number = CAPIENZA_STD): string {
  if (pal > cap + 2) return "bg-red-500";
  if (pal > cap) return "bg-amber-500";
  return "bg-emerald-500";
}

/** Chip mezzo: icona Truck/Ship in quadrato muted (~34px). */
export function MezzoChip({
  tipo,
  className,
}: {
  tipo?: string | null;
  className?: string;
}) {
  const Icon = tipo?.startsWith("container") ? Ship : Truck;
  return (
    <span
      className={cn(
        "inline-flex size-[34px] shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground",
        className
      )}
    >
      <Icon className="size-4" />
    </span>
  );
}

/** Tag regime doganale colorato. */
export function RegimeTag({
  regime,
  className,
}: {
  regime?: RegimePaese | null;
  className?: string;
}) {
  if (!regime) return null;
  const r = REGIME_TAG[regime];
  return (
    <span
      className={cn(
        "shrink-0 rounded-md px-1.5 py-px text-[11px] font-medium",
        r.cls,
        className
      )}
    >
      {r.label}
    </span>
  );
}

/** Barra di capienza + "{pal} / {cap}" con colore semaforico. Leggibile anche
 * compatta. `preview` (opzionale) mostra l'anteprima durante un drag. */
export function CapienzaBar({
  pallet,
  capacita = CAPIENZA_STD,
  preview,
  compact = false,
  className,
}: {
  pallet: number;
  capacita?: number;
  preview?: number | null;
  compact?: boolean;
  className?: string;
}) {
  const val = preview ?? pallet;
  const pct = Math.min(100, (val / capacita) * 100);
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <div
          className={cn(
            "h-1.5 flex-1 overflow-hidden rounded-full bg-muted",
            compact && "h-1"
          )}
        >
          <div
            className={cn("h-full rounded-full transition-all", capacityColor(val, capacita))}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={cn(
            "shrink-0 tabular-nums text-muted-foreground",
            compact ? "text-[11px]" : "text-xs"
          )}
        >
          {preview != null && preview !== pallet ? (
            <>
              {pallet} → <span className="font-medium text-foreground">{preview}</span>
            </>
          ) : (
            Math.round(val)
          )}{" "}
          / {capacita}
        </span>
      </div>
    </div>
  );
}
